import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api, apiErr } from "@/lib/api";
import { maskPhoneBR } from "@/lib/format";
import { Check, ArrowRight, Film, Sparkles, Star, Clapperboard, Tag, MessageCircle, Store, ChevronDown } from "lucide-react";

const fade = { initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] } };
const DEMO = "/urban-burger-lab";
const INCLUDED = ["Cardápio digital", "Link exclusivo", "QR Code", "Vídeo de capa", "Stories", "Menu em Vídeo", "Mais Vendidos", "Ofertas e promoções", "Produtos e categorias", "Adicionais", "Upsell e recomendações", "Carrinho", "Checkout", "Pedidos pelo WhatsApp", "Tema claro e escuro", "Painel de gerenciamento", "0% de comissão sobre pedidos"];

export default function Landing() {
  const [quote, setQuote] = useState(null); // null | interest string
  return (
    <div className="bg-[#0B0B0D] text-white overflow-x-hidden selection:bg-[#FF5A5F]/40">
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B0B0D]/70 border-b border-white/[0.06]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <img src="/hmmm-logo.png" alt="HMMM MENU" className="h-8 sm:h-9 w-auto" />
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="#precos" className="hidden sm:block text-sm text-zinc-400 hover:text-white transition-colors">Preço</a>
            <a href="#como" className="hidden sm:block text-sm text-zinc-400 hover:text-white transition-colors">Como funciona</a>
            <Link to="/login" data-testid="nav-login" className="text-sm font-semibold px-4 py-2 rounded-full bg-white/10 hover:bg-white/[0.16] transition-colors">Entrar</Link>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-[560px] w-[560px] rounded-full blur-[80px]" style={{ background: "radial-gradient(circle, rgba(255,90,95,0.20), transparent 68%)" }} />
          <div className="absolute top-40 -right-20 h-[360px] w-[360px] rounded-full blur-[90px]" style={{ background: "radial-gradient(circle, rgba(255,45,142,0.16), transparent 70%)" }} />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "22px 22px" }} />
        </div>

        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-8 items-center relative">
          <motion.div {...fade} className="text-center lg:text-left">
            <span className="inline-flex items-center gap-2 text-xs font-semibold bg-white/[0.06] border border-white/10 px-3.5 py-1.5 rounded-full text-zinc-300">
              <Sparkles size={13} className="hmmm-text" /> HMMM. O som do desejo antes do pedido.
            </span>
            <h1 className="font-head text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight mt-5 leading-[1.03]">
              Seu cardápio não precisa <span className="hmmm-text">parecer um cardápio</span>.
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 mt-5 max-w-lg mx-auto lg:mx-0 leading-relaxed">
              Não somos um sistema de gestão. Somos a experiência que transforma o desejo em pedido feita para chamar atenção e vender mais.
            </p>
            <div className="flex flex-wrap justify-center lg:justify-start gap-3 mt-8">
              <button onClick={() => setQuote("Assinatura HMMM MENU")} data-testid="cta-quote" className="inline-flex items-center gap-2 rounded-full hmmm-gradient px-6 py-3.5 font-bold hmmm-glow active:scale-95 transition-transform">
                Quero meu HMMM MENU <ArrowRight size={18} />
              </button>
              <a href={DEMO} data-testid="cta-demo" className="inline-flex items-center gap-2 rounded-full bg-white/[0.08] border border-white/10 px-6 py-3.5 font-bold hover:bg-white/[0.14] transition-colors">Ver demonstração</a>
            </div>
            <div className="flex justify-center lg:justify-start gap-8 sm:gap-10 mt-10">
              <Metric value="+35%" label="ticket médio" />
              <Metric value="0%" label="taxas por pedido" />
              <Metric value="1 link" label="pronto pra vender" />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30, rotate: -2 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="flex justify-center">
            <PhoneLive />
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <motion.h2 {...fade} className="font-head text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">Uma experiência interativa, do primeiro toque ao pedido.</motion.h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 mt-10">
          <Feature icon={<Film />} title="Capa em vídeo" desc="Abra o apetite com uma capa cinematográfica em vídeo autoplay." />
          <Feature icon={<Sparkles />} title="Stories" desc="Novidades e promoções em stories fullscreen com CTA de compra." />
          <Feature icon={<Star />} title="Mais Vendidos" desc="Destaque os queridinhos num carrossel automático que converte." />
          <Feature icon={<Clapperboard />} title="Menu em Vídeo" desc="Mostre o prato sendo montado e leve direto ao produto." />
          <Feature icon={<Tag />} title="Ofertas" desc="Preço antigo, novo e a economia calculada automaticamente." />
          <Feature icon={<MessageCircle />} title="Venda Guiada" desc="'Complete seu pedido' com recomendações na tela e no carrinho." />
        </div>
      </section>

      {/* PANEL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
          <motion.div {...fade} className="order-2 lg:order-1 rounded-3xl border border-white/10 bg-[#141417] p-6 sm:p-8">
            {["Visão Geral", "Minha Loja", "Produtos & Categorias", "Conteúdo (Stories, Vídeos, Ofertas)", "Conversão / Upsell", "Atendimento & Link"].map((m) => (
              <div key={m} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
                <span className="h-6 w-6 rounded-lg hmmm-gradient flex items-center justify-center shrink-0"><Store size={13} /></span>
                <span className="text-sm text-zinc-300">{m}</span>
              </div>
            ))}
          </motion.div>
          <motion.div {...fade} className="order-1 lg:order-2">
            <span className="text-xs font-bold tracking-[0.2em] hmmm-text">O PAINEL</span>
            <h2 className="font-head text-3xl sm:text-4xl font-bold tracking-tight mt-2">Você constrói a experiência. Sem código.</h2>
            <p className="text-zinc-400 mt-4 leading-relaxed">Edite tudo em minutos: fotos e vídeos reais do seu celular, produtos, adicionais, ofertas e upsells. Um único template premium HMMM MENU, personalizado com a identidade da sua marca — em tema escuro ou claro.</p>
          </motion.div>
        </div>
      </section>

      {/* HOW */}
      <section id="como" className="max-w-7xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <motion.h2 {...fade} className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-center">Como funciona</motion.h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {[["01", "Solicite um orçamento", "Conte sobre seu restaurante."],
            ["02", "Implantação personalizada", "Montamos seu cardápio premium."],
            ["03", "Ativação da conta", "Você recebe acesso ao painel."],
            ["04", "Comece a vender", "Compartilhe seu link e QR Code."]].map(([n, t, d]) => (
            <motion.div key={n} {...fade} className="rounded-2xl border border-white/10 bg-[#141417] p-6 hover:border-[#FF5A5F]/30 transition-colors">
              <span className="font-head text-3xl font-extrabold hmmm-text">{n}</span>
              <h3 className="font-head font-bold mt-3">{t}</h3>
              <p className="text-sm text-zinc-400 mt-1">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section id="precos" className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <motion.div {...fade} className="mx-auto max-w-2xl rounded-3xl border border-[#FF5A5F]/30 bg-gradient-to-b from-[#1a1013] to-[#141417] p-7 sm:p-10 text-center relative overflow-hidden">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 h-44 w-44 rounded-full blur-2xl" style={{ background: "radial-gradient(circle, rgba(255,74,110,0.30), transparent 70%)" }} />
          <span className="text-xs font-bold tracking-[0.2em] hmmm-text relative">HMMM MENU</span>
          <div className="mt-3 flex items-end justify-center gap-1 relative"><span className="font-head text-5xl sm:text-6xl font-extrabold">R$ 49,90</span><span className="text-zinc-400 mb-2">/mês</span></div>
          <p className="text-zinc-300 mt-3 relative max-w-md mx-auto">Seu cardápio completo, visual e feito para transformar desejo em pedido.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-x-6 gap-y-2.5 text-left max-w-lg mx-auto relative">
            {INCLUDED.map((f) => (
              <div key={f} className="flex items-center gap-2.5"><Check size={16} className="text-[#FF6B00] shrink-0" /><span className="text-sm text-zinc-200">{f}</span></div>
            ))}
          </div>
          <button onClick={() => setQuote("Assinatura HMMM MENU")} data-testid="pricing-quote" className="mt-8 w-full rounded-full hmmm-gradient py-4 font-bold hmmm-glow active:scale-[0.98] transition-transform relative">Quero meu HMMM MENU</button>
          <p className="text-xs text-zinc-400 mt-4 relative">Sem taxa de implantação obrigatória. Você pode montar seu cardápio sozinho e pagar apenas R$ 49,90/mês.</p>
        </motion.div>

        {/* IMPLANTAÇÃO */}
        <div className="text-center mt-16 mb-8">
          <motion.h2 {...fade} className="font-head text-3xl sm:text-4xl font-bold tracking-tight">Quer receber seu cardápio pronto?</motion.h2>
          <motion.p {...fade} className="text-zinc-400 mt-3 max-w-xl mx-auto">A assinatura dá acesso a todas as ferramentas. Se preferir, nossa equipe também pode preparar seu cardápio no padrão visual HMMM MENU.</motion.p>
        </div>
        <div className="grid md:grid-cols-2 gap-4 md:gap-5 items-start">
          <PlanCard
            testid="impl-essential"
            title="Implantação Essencial"
            price="R$ 179,90"
            note="pagamento único"
            features={["1 vídeo de capa", "5 Stories editados no padrão HMMM MENU", "5 produtos preparados para os destaques/carrossel", "3 conteúdos para Menu em Vídeo", "Configuração inicial do cardápio"]}
            cta="Quero a Implantação Essencial"
            onClick={() => setQuote("Implantação Essencial")}
          />
          <PlanCard
            testid="impl-complete"
            featured
            title="Implantação Completa"
            price="R$ 299,90"
            note="pagamento único · valor fixo"
            features={["Montagem completa do cardápio", "Organização de todas as categorias", "Cadastro e configuração dos produtos", "Tratamento e padronização das imagens", "Descrições dos produtos", "Vídeo de capa, Stories e Menu em Vídeo", "Mais Vendidos, ofertas e promoções", "Adicionais e upsell/recomendações", "Informações da loja, horários e atendimento", "Revisão final — cardápio pronto para divulgação"]}
            cta="Quero meu cardápio completo"
            onClick={() => setQuote("Implantação Completa")}
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-16 md:py-24">
        <motion.h2 {...fade} className="font-head text-3xl sm:text-4xl font-bold tracking-tight text-center mb-8">Perguntas frequentes</motion.h2>
        {[["Preciso me cadastrar sozinho?", "Não. Nossa equipe cria e ativa sua conta após a contratação e implantação."],
          ["O cliente final paga alguma taxa?", "Não cobramos taxa por pedido. Os pedidos vão direto ao seu WhatsApp."],
          ["Posso usar meus próprios vídeos e fotos?", "Sim, você faz upload real direto do celular, com preview e progresso."],
          ["Consigo deixar o cardápio no tema claro?", "Sim. Você escolhe entre tema escuro ou claro no painel, em Minha Loja."],
          ["Existe fidelidade?", "Não. A mensalidade é de R$ 49,90/mês e você cancela quando quiser."]].map(([q, a]) => <Faq key={q} q={q} a={a} />)}
      </section>

      {/* FINAL CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
        <motion.div {...fade} className="rounded-3xl hmmm-gradient p-8 sm:p-14 text-center relative overflow-hidden">
          <div className="absolute -right-8 -bottom-10 opacity-20"><img src="/hmmm-icon.png" alt="" className="h-44 w-44" /></div>
          <h2 className="font-head text-3xl sm:text-4xl font-extrabold text-white max-w-2xl mx-auto relative">Pronto para transformar seu cardápio numa máquina de desejo?</h2>
          <button onClick={() => setQuote("Assinatura HMMM MENU")} data-testid="final-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-black/90 text-white px-8 py-4 font-bold hover:bg-black transition-colors relative">Quero meu HMMM MENU <ArrowRight size={18} /></button>
        </motion.div>
      </section>

      <footer className="border-t border-white/5 py-10 text-center">
        <img src="/hmmm-logo.png" alt="HMMM MENU" className="h-8 w-auto mx-auto opacity-90" />
        <p className="text-sm text-zinc-500 mt-3 italic">HMMM. O som do desejo antes do pedido.</p>
      </footer>

      {quote && <QuoteModal interest={quote} onClose={() => setQuote(null)} />}
    </div>
  );
}

function PlanCard({ testid, title, price, note, features, cta, onClick, featured }) {
  return (
    <motion.div {...fade} data-testid={`${testid}-card`}
      className={`relative rounded-3xl p-6 sm:p-7 ${featured ? "border border-[#FF5A5F]/40 bg-gradient-to-b from-[#1a1013] to-[#141417] shadow-2xl" : "border border-white/10 bg-[#141417]"}`}>
      <div className="relative flex items-start">
        <h3 className="font-head text-xl font-extrabold leading-tight pr-[92px]">{title}</h3>
        <span className="absolute right-0 top-0 shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full hmmm-gradient text-white">1º mês grátis</span>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-x-1.5 gap-y-0.5">
        <span className="font-head text-4xl font-extrabold whitespace-nowrap">{price}</span>
        <span className="text-xs text-zinc-400 mb-1.5">{note}</span>
      </div>
      <p className="text-xs text-zinc-400 mt-1">Depois, R$ 49,90/mês a partir do 2º mês.</p>
      <div className="mt-5 space-y-2.5">
        {features.map((f) => (
          <div key={f} className="flex items-start gap-2.5"><Check size={16} className="text-[#FF6B00] shrink-0 mt-0.5" /><span className="text-sm text-zinc-200">{f}</span></div>
        ))}
      </div>
      <button onClick={onClick} data-testid={`${testid}-cta`}
        className={`mt-6 w-full rounded-full py-3.5 font-bold active:scale-[0.98] transition-transform ${featured ? "hmmm-gradient hmmm-glow text-white" : "bg-white/[0.08] border border-white/10 hover:bg-white/[0.14]"}`}>{cta}</button>
    </motion.div>
  );
}

function Metric({ value, label }) {
  return <div><p className="font-head text-2xl font-extrabold hmmm-text">{value}</p><p className="text-xs text-zinc-500">{label}</p></div>;
}
function Feature({ icon, title, desc }) {
  return (
    <motion.div {...fade} className="rounded-2xl border border-white/10 bg-[#141417] p-6 hover:border-[#FF5A5F]/30 hover:-translate-y-0.5 transition-all">
      <div className="h-11 w-11 rounded-xl hmmm-gradient text-white flex items-center justify-center">{icon}</div>
      <h3 className="font-head font-bold mt-4">{title}</h3>
      <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{desc}</p>
    </motion.div>
  );
}
function Faq({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-4 py-5 text-left">
        <span className="font-semibold">{q}</span><ChevronDown size={18} className={`shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="text-sm text-zinc-400 pb-5 -mt-1">{a}</p>}
    </div>
  );
}

// Live preview of the real public menu, scaled to fit the phone frame without any clipping/squish.
function PhoneLive() {
  const FRAME_W = 300, BORDER = 8;
  const LOGICAL_W = 390; // real mobile width the menu is designed for
  const scale = (FRAME_W - BORDER * 2) / LOGICAL_W; // ≈ 0.728
  const INNER_H = 604; // visible height inside the frame
  const LOGICAL_H = Math.round(INNER_H / scale);
  const frameRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  // Lazy-mount the live iframe near the viewport (perf). AFTER mount the mockup
  // uses the exact original visual structure (fixed geometry, plain rounded
  // overflow-hidden) — no contain/translateZ/isolation, so corners clip cleanly.
  useEffect(() => {
    const el = frameRef.current;
    if (!el || mounted) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setMounted(true); io.disconnect(); }
    }, { rootMargin: "300px" });
    io.observe(el);
    return () => io.disconnect();
  }, [mounted]);
  return (
    <div className="relative">
      <div className="absolute -inset-6 rounded-[3rem] blur-3xl opacity-40 hmmm-gradient" />
      <div ref={frameRef} className="relative rounded-[2.75rem] bg-zinc-900 shadow-2xl" style={{ width: FRAME_W, padding: BORDER, border: "1px solid rgba(255,255,255,0.08)" }}>
        <div className="relative rounded-[2.2rem] bg-black overflow-hidden" style={{ height: INNER_H }}>
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-28 h-6 bg-black rounded-b-2xl z-10" />
          {mounted ? (
            <iframe
              src={DEMO}
              title="Cardápio HMMM MENU"
              scrolling="no"
              loading="lazy"
              className="pointer-events-none select-none origin-top-left"
              style={{ width: LOGICAL_W, height: LOGICAL_H, transform: `scale(${scale})` }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="h-8 w-8 rounded-full border-2 border-white/20 border-t-[#FF5A5F] animate-spin" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const WA_MSG = {
  "Assinatura HMMM MENU": (n, r) => `Olá! Vim pelo site da HMMM MENU 👋\n\nMeu nome é ${n} e meu restaurante é ${r}.\n\nQuero contratar a HMMM MENU por R$ 49,90/mês e montar meu cardápio por conta própria.\n\nQuero dar continuidade à contratação.`,
  "Implantação Essencial": (n, r) => `Olá! Vim pelo site da HMMM MENU 👋\n\nMeu nome é ${n} e meu restaurante é ${r}.\n\nTenho interesse na Implantação Essencial por R$ 179,90, com o primeiro mês da HMMM MENU grátis e mensalidade de R$ 49,90 a partir do segundo mês.\n\nQuero dar continuidade à contratação.`,
  "Implantação Completa": (n, r) => `Olá! Vim pelo site da HMMM MENU 👋\n\nMeu nome é ${n} e meu restaurante é ${r}.\n\nTenho interesse na Implantação Completa por R$ 299,90, com o primeiro mês da HMMM MENU grátis e mensalidade de R$ 49,90 a partir do segundo mês.\n\nQuero dar continuidade à contratação.`,
};

function QuoteModal({ onClose, interest = "Assinatura HMMM MENU" }) {
  const [name, setName] = useState("");
  const [restaurant, setRestaurant] = useState("");
  const [wa, setWa] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !restaurant.trim() || wa.replace(/\D/g, "").length < 10) { toast.error("Preencha seu nome, o restaurante e um WhatsApp válido"); return; }
    setLoading(true);
    try {
      await api.post("/leads", { restaurant_name: restaurant, contact_name: name, whatsapp: wa, interest, origin: "Landing Page" });
    } catch (err) {
      setLoading(false);
      if (err?.response?.status === 429) {
        toast.error(err.response.data?.detail || "Você enviou várias solicitações em pouco tempo. Aguarde alguns minutos e tente novamente.");
        return;
      }
      toast.error(apiErr(err));
      return;
    }
    const build = WA_MSG[interest] || WA_MSG["Assinatura HMMM MENU"];
    window.open(`https://wa.me/5543984586724?text=${encodeURIComponent(build(name, restaurant))}`, "_blank");
    toast.success("Solicitação enviada! Abrindo o WhatsApp...");
    setLoading(false);
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center px-6" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="w-full max-w-sm bg-[#141417] border border-white/10 rounded-3xl p-7" data-testid="quote-modal">
        <img src="/hmmm-logo.png" alt="HMMM MENU" className="h-8 w-auto mb-4" />
        <h3 className="font-head text-xl font-extrabold">Solicitar contato</h3>
        <p className="text-sm mt-1 mb-1 hmmm-text font-semibold" data-testid="quote-interest">{interest}</p>
        <p className="text-sm text-zinc-500 mb-5">Deixe seus dados e continuamos no WhatsApp.</p>
        <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Seu nome" data-testid="quote-name" className="w-full mb-3 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 outline-none focus:border-[#FF5A5F]" />
        <input value={restaurant} onChange={(e) => setRestaurant(e.target.value)} required placeholder="Nome do restaurante" data-testid="quote-restaurant" className="w-full mb-3 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 outline-none focus:border-[#FF5A5F]" />
        <input value={wa} onChange={(e) => setWa(maskPhoneBR(e.target.value))} required inputMode="tel" placeholder="WhatsApp para contato" data-testid="quote-whatsapp" className="w-full mb-5 rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 outline-none focus:border-[#FF5A5F]" />
        <button disabled={loading} data-testid="quote-submit" className="w-full rounded-full hmmm-gradient py-3.5 font-bold hmmm-glow disabled:opacity-60">{loading ? "Enviando..." : "Enviar e ir para o WhatsApp"}</button>
        <button type="button" onClick={onClose} className="w-full text-center text-xs text-zinc-400 mt-3">Cancelar</button>
      </form>
    </div>
  );
}
